import React, { useState, useEffect } from 'react';
import { api } from '../../services/api';
import CommsHealthWidget from './CommsHealthWidget';

function DealerProfile() {
  const [profile, setProfile] = useState({
    name: '',
    abn: '',
    address: '',
    phone: '',
    email: '',
    opening_hours: {
      monday: { open: '09:00', close: '17:00', closed: false },
      tuesday: { open: '09:00', close: '17:00', closed: false },
      wednesday: { open: '09:00', close: '17:00', closed: false },
      thursday: { open: '09:00', close: '17:00', closed: false },
      friday: { open: '09:00', close: '17:00', closed: false },
      saturday: { open: '09:00', close: '12:00', closed: false },
      sunday: { open: '09:00', close: '17:00', closed: true },
    },
    brand_colors: {
      primary: '#007bff',
      secondary: '#6c757d',
    },
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const data = await api.getDealerProfile();
      setProfile(prev => ({
        ...prev,
        ...data,
        opening_hours: { ...prev.opening_hours, ...data.opening_hours },
        brand_colors: { ...prev.brand_colors, ...data.brand_colors },
      }));
    } catch (error) {
      console.error('Failed to load profile:', error);
      setMessage({ type: 'error', text: 'Failed to load dealer profile' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setProfile(prev => ({ ...prev, [name]: value }));
  };

  const handleHoursChange = (day, field, value) => {
    setProfile(prev => ({
      ...prev,
      opening_hours: {
        ...prev.opening_hours,
        [day]: {
          ...prev.opening_hours[day],
          [field]: value,
        },
      },
    }));
  };

  const handleColorChange = (colorType, value) => {
    setProfile(prev => ({
      ...prev,
      brand_colors: {
        ...prev.brand_colors,
        [colorType]: value,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage(null);

    try {
      await api.updateDealerProfile(profile);
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
    } catch (error) {
      console.error('Failed to save:', error);
      setMessage({ type: 'error', text: error.message || 'Failed to save profile' });
    } finally {
      setSaving(false);
    }
  };

  const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

  if (loading) {
    return <div style={styles.loading}>Loading profile...</div>;
  }

  return (
    <div style={styles.container}>
      <h1 style={styles.title}>🏢 Dealer Group Profile</h1>

      <CommsHealthWidget />
      
      {message && (
        <div style={{
          ...styles.message,
          backgroundColor: message.type === 'success' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(239, 68, 68, 0.2)',
          color: message.type === 'success' ? '#86efac' : '#fca5a5',
        }}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} style={styles.form}>
        {/* Basic Information */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>Basic Information</h2>
          
          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Dealer Name *</label>
              <input
                type="text"
                name="name"
                value={profile.name}
                onChange={handleChange}
                required
                style={styles.input}
                placeholder="ABC Motors"
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>ABN</label>
              <input
                type="text"
                name="abn"
                value={profile.abn || ''}
                onChange={handleChange}
                style={styles.input}
                placeholder="12 345 678 901"
              />
            </div>
          </div>

          <div style={styles.formGroup}>
            <label style={styles.label}>Address</label>
            <textarea
              name="address"
              value={profile.address || ''}
              onChange={handleChange}
              style={styles.textarea}
              placeholder="123 Main Street, Sydney NSW 2000"
              rows={2}
            />
          </div>

          <div style={styles.formRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Phone</label>
              <input
                type="tel"
                name="phone"
                value={profile.phone || ''}
                onChange={handleChange}
                style={styles.input}
                placeholder="(02) 9123 4567"
              />
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Email</label>
              <input
                type="email"
                name="email"
                value={profile.email || ''}
                onChange={handleChange}
                style={styles.input}
                placeholder="sales@abcmotors.com.au"
              />
            </div>
          </div>
        </section>

        {/* Opening Hours */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>🕐 Opening Hours</h2>
          
          <div style={styles.hoursGrid}>
            {days.map(day => (
              <div key={day} style={styles.hoursRow}>
                <span style={styles.dayLabel}>
                  {day.charAt(0).toUpperCase() + day.slice(1)}
                </span>
                
                <label style={styles.checkboxLabel}>
                  <input
                    type="checkbox"
                    checked={!profile.opening_hours[day]?.closed}
                    onChange={(e) => handleHoursChange(day, 'closed', !e.target.checked)}
                    style={styles.checkbox}
                  />
                  Open
                </label>
                
                {!profile.opening_hours[day]?.closed && (
                  <>
                    <input
                      type="time"
                      value={profile.opening_hours[day]?.open || '09:00'}
                      onChange={(e) => handleHoursChange(day, 'open', e.target.value)}
                      style={styles.timeInput}
                    />
                    <span style={styles.timeSeparator}>to</span>
                    <input
                      type="time"
                      value={profile.opening_hours[day]?.close || '17:00'}
                      onChange={(e) => handleHoursChange(day, 'close', e.target.value)}
                      style={styles.timeInput}
                    />
                  </>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* Brand Colors */}
        <section style={styles.section}>
          <h2 style={styles.sectionTitle}>🎨 Brand Colors</h2>
          
          <div style={styles.colorRow}>
            <div style={styles.formGroup}>
              <label style={styles.label}>Primary Color</label>
              <div style={styles.colorInput}>
                <input
                  type="color"
                  value={profile.brand_colors?.primary || '#007bff'}
                  onChange={(e) => handleColorChange('primary', e.target.value)}
                  style={styles.colorPicker}
                />
                <input
                  type="text"
                  value={profile.brand_colors?.primary || '#007bff'}
                  onChange={(e) => handleColorChange('primary', e.target.value)}
                  style={{ ...styles.input, width: '100px' }}
                />
              </div>
            </div>
            
            <div style={styles.formGroup}>
              <label style={styles.label}>Secondary Color</label>
              <div style={styles.colorInput}>
                <input
                  type="color"
                  value={profile.brand_colors?.secondary || '#6c757d'}
                  onChange={(e) => handleColorChange('secondary', e.target.value)}
                  style={styles.colorPicker}
                />
                <input
                  type="text"
                  value={profile.brand_colors?.secondary || '#6c757d'}
                  onChange={(e) => handleColorChange('secondary', e.target.value)}
                  style={{ ...styles.input, width: '100px' }}
                />
              </div>
            </div>
          </div>
        </section>

        <button type="submit" disabled={saving} style={styles.submitButton}>
          {saving ? 'Saving...' : '💾 Save Profile'}
        </button>
      </form>
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '800px',
  },
  title: {
    color: '#f1f5f9',
    margin: '0 0 24px 0',
    fontSize: '24px',
    fontWeight: '600',
  },
  message: {
    padding: '12px 16px',
    borderRadius: '8px',
    marginBottom: '20px',
    fontSize: '14px',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
  },
  section: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '24px',
    border: '1px solid #334155',
  },
  sectionTitle: {
    color: '#f1f5f9',
    margin: '0 0 20px 0',
    fontSize: '16px',
    fontWeight: '600',
  },
  formRow: {
    display: 'flex',
    gap: '16px',
    marginBottom: '16px',
  },
  formGroup: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  label: {
    color: '#94a3b8',
    fontSize: '13px',
    fontWeight: '500',
  },
  input: {
    padding: '10px 12px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#f1f5f9',
    fontSize: '14px',
    outline: 'none',
  },
  textarea: {
    padding: '10px 12px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '6px',
    color: '#f1f5f9',
    fontSize: '14px',
    outline: 'none',
    resize: 'vertical',
    fontFamily: 'inherit',
  },
  hoursGrid: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  hoursRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    padding: '8px 0',
    borderBottom: '1px solid #334155',
  },
  dayLabel: {
    width: '100px',
    color: '#f1f5f9',
    fontWeight: '500',
    textTransform: 'capitalize',
  },
  checkboxLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#94a3b8',
    fontSize: '14px',
    cursor: 'pointer',
    width: '80px',
  },
  checkbox: {
    cursor: 'pointer',
  },
  timeInput: {
    padding: '6px 8px',
    background: '#0f172a',
    border: '1px solid #334155',
    borderRadius: '4px',
    color: '#f1f5f9',
    fontSize: '13px',
  },
  timeSeparator: {
    color: '#64748b',
    fontSize: '13px',
  },
  colorRow: {
    display: 'flex',
    gap: '24px',
  },
  colorInput: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  colorPicker: {
    width: '48px',
    height: '40px',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    background: 'transparent',
  },
  submitButton: {
    padding: '14px 24px',
    background: '#3b82f6',
    color: '#fff',
    border: 'none',
    borderRadius: '8px',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    alignSelf: 'flex-start',
  },
  loading: {
    color: '#94a3b8',
    fontSize: '14px',
  },
};

export default DealerProfile;
